<script setup lang="ts">
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import {
  getPlaybookStatistics,
  PlaybookActionType,
  PlaybookEvaluationType,
  type PlaybookStatistics,
} from '@/api';
import StatisticsBarChartCard from '@/components/admin/StatisticsBarChartCard.vue';
import StatisticsStackedBarChartCard, {
  type StackedSeries,
} from '@/components/admin/StatisticsStackedBarChartCard.vue';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsKeyLabel,
  formatStatisticsNumber,
  sortStatisticsEntries,
  type StatisticsCountMap,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';

type BarSection = {
  title: string;
  keyLabel: string;
  valueLabel: string;
  rows: [string, number][];
};

type CrossTab = Partial<Record<PlaybookEvaluationType, StatisticsCountMap>>;

type StackedSection = {
  title: string;
  categories: string[];
  series: StackedSeries[];
};

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<PlaybookStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    { label: 'Total playbooks', value: formatStatisticsNumber(data.value.total_playbooks) },
    { label: 'Total runs', value: formatStatisticsNumber(data.value.total_runs) },
    {
      label: 'Avg runs / playbook',
      value: formatStatisticsNumber(data.value.average_runs_per_playbook),
    },
    {
      label: 'Configured evaluations',
      value: formatStatisticsNumber(data.value.total_configured_evaluations),
    },
    {
      label: 'Avg evaluations / playbook',
      value: formatStatisticsNumber(data.value.average_configured_evaluations_per_playbook),
    },
    {
      label: 'Avg actions / evaluation',
      value: formatStatisticsNumber(data.value.average_configured_actions_per_evaluation),
    },
    {
      label: 'Evaluation results',
      value: formatStatisticsNumber(data.value.total_evaluation_results),
    },
    {
      label: 'Avg evaluation results / run',
      value: formatStatisticsNumber(data.value.average_evaluation_results_per_run),
    },
    {
      label: 'Avg action results / result',
      value: formatStatisticsNumber(data.value.average_action_results_per_evaluation_result),
    },
  ];
});

const evaluationSections = computed<BarSection[]>(() => [
  {
    title: 'Configured evaluations by type',
    keyLabel: 'Evaluation type',
    valueLabel: 'Evaluations',
    rows: sortStatisticsEntries(data.value?.configured_evaluations_by_type, 'value-desc'),
  },
  {
    title: 'Evaluation results by type',
    keyLabel: 'Evaluation type',
    valueLabel: 'Results',
    rows: sortStatisticsEntries(data.value?.evaluation_results_by_type, 'value-desc'),
  },
]);

function buildStackedSection(title: string, crossTab: CrossTab | undefined): StackedSection {
  const evaluationTypes = Object.keys(crossTab ?? {}).filter(key =>
    isEnumValue(PlaybookEvaluationType, key)
  );
  const categories = evaluationTypes.map(formatStatisticsKeyLabel);

  // Colors come from the chart card's palette slots, keyed by series order —
  // the action-type order is fixed, so colors stay stable across both charts.
  const series = Object.values(PlaybookActionType).map<StackedSeries>(actionType => ({
    name: formatStatisticsKeyLabel(actionType),
    values: evaluationTypes.map(evaluationType => crossTab?.[evaluationType]?.[actionType] ?? 0),
  }));

  return { title, categories, series };
}

const stackedSections = computed<StackedSection[]>(() => [
  buildStackedSection(
    'Configured actions per evaluation',
    data.value?.configured_actions_by_eval_and_action_type
  ),
  buildStackedSection(
    'Action results per evaluation',
    data.value?.action_results_by_eval_and_action_type
  ),
]);

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getPlaybookStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });

    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load playbook statistics');
    logger.error('Failed to load playbook statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <n-flex v-if="data !== undefined" vertical :size="16">
      <statistics-kpi-grid :tiles="kpiTiles" />

      <n-grid cols="1 s:1 m:2" responsive="screen" :x-gap="16" :y-gap="16">
        <n-gi v-for="section in evaluationSections" :key="section.title">
          <statistics-bar-chart-card
            :title="section.title"
            :rows="section.rows"
            :x-label="section.valueLabel"
            :y-label="section.keyLabel"
            :horizontal="true"
            :format-category="formatStatisticsKeyLabel"
          />
        </n-gi>
      </n-grid>

      <n-grid cols="1 s:1 m:2" responsive="screen" :x-gap="16" :y-gap="16">
        <n-gi v-for="section in stackedSections" :key="section.title">
          <statistics-stacked-bar-chart-card
            :title="section.title"
            :categories="section.categories"
            :series="section.series"
            x-label="Evaluation type"
            y-label="Actions"
          />
        </n-gi>
      </n-grid>
    </n-flex>
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
